import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../auth/AuthProvider";
import { notificationService } from "../../../services/notificationService";
import {
  CVNotUpdatedNotificationContent,
  NotificationQuery,
  NotificationType,
  ParsedNotification,
} from "../models";
import {
  getNotificationMessages,
  isUrgentNotification,
  NotificationMessage,
} from "../utils/notificationMessages";
import { safeParseNotifications } from "../utils/notificationParser";

interface UseParsedNotificationsState {
  notifications: ParsedNotification[];
  messages: NotificationMessage[];
  loading: boolean;
  error: string | null;
  unreadCount: number;
  urgentCount: number;
}

interface UseParsedNotificationsActions {
  fetchNotifications: (query?: NotificationQuery) => Promise<void>;
  markAsRead: (notificationId: number) => Promise<void>;
  markMultipleAsRead: (notificationIds: number[]) => Promise<void>;
  refreshUnreadCount: () => Promise<void>;
  clearError: () => void;
  getNotificationsByType: (type: NotificationType) => ParsedNotification[];
  getUrgentNotifications: () => ParsedNotification[];
}

interface UseParsedNotificationsReturn
  extends UseParsedNotificationsState,
    UseParsedNotificationsActions {}

export const useParsedNotifications = (
  initialQuery?: NotificationQuery
): UseParsedNotificationsReturn => {
  const { isAuthenticated, currentUser } = useAuth();
  const [notifications, setNotifications] = useState<ParsedNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to create local CV creation notification
  const createCVCreationNotification = useCallback((): ParsedNotification => {
    if (!currentUser)
      throw new Error("Current user is required to create CV notification");

    return {
      id: -1, // Special ID for local notification
      type: NotificationType.CVNotUpdated,
      content: {
        personId: 0,
        personEmail: currentUser.email,
        personFirstName: currentUser.firstName || "User",
        personLastName: currentUser.lastName || "",
        lastUpdateDate: new Date().toISOString(),
        daysSinceUpdate: 0,
        thresholdDays: 0,
      } as CVNotUpdatedNotificationContent,
      personId: 0,
      isRead: false,
      createdAt: new Date().toISOString(),
      readAt: undefined,
      rawContent: JSON.stringify({
        message:
          "To use all functionalities of the system, please create your own CV. It doesn't have to be complete, but it's required to have your basic information.",
      }),
    };
  }, [currentUser]);

  const fetchNotifications = useCallback(
    async (query?: NotificationQuery) => {
      // Skip API calls if user doesn't have personId
      if (
        !isAuthenticated ||
        !currentUser?.personId ||
        currentUser.hasPersonId === false
      ) {
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const rawNotifications =
          await notificationService.getPersonNotifications(
            currentUser.personId,
            query || initialQuery
          );
        const parsedNotifications = safeParseNotifications(rawNotifications);

        setNotifications(parsedNotifications);
      } catch (err) {
        // Handle HTTP 401 as expected behavior (user not authorized for notifications)
        if (
          (err as any)?.message?.includes("401") ||
          (err as any)?.status === 401
        ) {
          setNotifications([]);
          setError(null);
        } else {
          console.error("Error fetching notifications:", err);
          setError(
            err instanceof Error ? err.message : "Failed to fetch notifications"
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [
      isAuthenticated,
      currentUser?.personId,
      currentUser?.hasPersonId,
      initialQuery,
    ]
  );

  // Memoized computed values
  const messages = useMemo(() => {
    return getNotificationMessages(notifications);
  }, [notifications]);

  const urgentCount = useMemo(() => {
    return notifications.filter(
      (notification) =>
        !notification.isRead && isUrgentNotification(notification)
    ).length;
  }, [notifications]);

  const localUnreadCount = useMemo(() => {
    return notifications.filter((notification) => !notification.isRead).length;
  }, [notifications]);

  const markAsRead = useCallback(
    async (notificationId: number) => {
      // Don't allow marking local notifications as read via API
      if (notificationId === -1) {
        // Update local state only
        setNotifications((prev) =>
          prev.map((notification) =>
            notification.id === notificationId
              ? {
                  ...notification,
                  isRead: true,
                  readAt: new Date().toISOString(),
                }
              : notification
          )
        );
        return;
      }

      // Skip API calls if user doesn't have personId
      if (
        !isAuthenticated ||
        !currentUser?.personId ||
        currentUser.hasPersonId === false
      ) {
        return;
      }

      try {
        setError(null);
        await notificationService.markNotificationAsRead(notificationId);

        // Update local state
        setNotifications((prev) =>
          prev.map((notification) =>
            notification.id === notificationId
              ? {
                  ...notification,
                  isRead: true,
                  readAt: new Date().toISOString(),
                }
              : notification
          )
        );

        await refreshUnreadCount();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to mark notification as read"
        );
      }
    },
    [isAuthenticated, currentUser?.personId, currentUser?.hasPersonId]
  );

  const markMultipleAsRead = useCallback(
    async (notificationIds: number[]) => {
      // Filter out local notifications and handle them separately
      const localNotificationIds = notificationIds.filter((id) => id === -1);
      const apiNotificationIds = notificationIds.filter((id) => id !== -1);

      // Update local notifications
      if (localNotificationIds.length > 0) {
        setNotifications((prev) =>
          prev.map((notification) =>
            notification.id === -1
              ? {
                  ...notification,
                  isRead: true,
                  readAt: new Date().toISOString(),
                }
              : notification
          )
        );
      }

      // Skip API calls if user doesn't have personId or no API notifications to mark
      if (
        !isAuthenticated ||
        !currentUser?.personId ||
        currentUser.hasPersonId === false ||
        apiNotificationIds.length === 0
      ) {
        return;
      }

      try {
        setError(null);
        await notificationService.markMultipleAsRead(apiNotificationIds);

        // Update local state for API notifications
        setNotifications((prev) =>
          prev.map((notification) =>
            apiNotificationIds.includes(notification.id)
              ? {
                  ...notification,
                  isRead: true,
                  readAt: new Date().toISOString(),
                }
              : notification
          )
        );

        await refreshUnreadCount();
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to mark notifications as read"
        );
      }
    },
    [isAuthenticated, currentUser?.personId, currentUser?.hasPersonId]
  );

  const refreshUnreadCount = useCallback(async () => {
    // Skip API calls if user doesn't have personId
    if (
      !isAuthenticated ||
      !currentUser?.personId ||
      currentUser.hasPersonId === false
    ) {
      return;
    }

    // This function is kept for compatibility but now relies on local calculation
    // The unread count is now calculated locally from the notifications array
    try {
      await notificationService.getPersonUnreadCount(currentUser.personId);
    } catch (err) {
      // Handle HTTP 401 as expected behavior (user not authorized for notifications)
      if (
        !(
          (err as any)?.message?.includes("401") || (err as any)?.status === 401
        )
      ) {
        console.error("Failed to refresh unread count:", err);
      }
    }
  }, [isAuthenticated, currentUser?.personId, currentUser?.hasPersonId]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const getNotificationsByType = useCallback(
    (type: NotificationType) => {
      return notifications.filter((notification) => notification.type === type);
    },
    [notifications]
  );

  const getUrgentNotifications = useCallback(() => {
    return notifications.filter(isUrgentNotification);
  }, [notifications]);

  // Main effect to handle notifications based on authentication and personId status
  useEffect(() => {
    if (!isAuthenticated || !currentUser) {
      setNotifications([]);
      return;
    }

    // If user has no personId (hasPersonId === false), show local CV creation notification
    if (currentUser.hasPersonId === false) {
      const urgentNotification = createCVCreationNotification();
      setNotifications([urgentNotification]);
      return;
    }

    // If user has personId, fetch notifications from API
    if (currentUser.personId) {
      fetchNotifications();
      refreshUnreadCount();
      return;
    }

    // Clear notifications if user state is unclear
    setNotifications([]);
  }, [
    isAuthenticated,
    currentUser?.personId,
    currentUser?.hasPersonId,
    fetchNotifications,
    refreshUnreadCount,
    createCVCreationNotification,
  ]);

  return {
    notifications,
    messages,
    loading,
    error,
    unreadCount: localUnreadCount,
    urgentCount,
    fetchNotifications,
    markAsRead,
    markMultipleAsRead,
    refreshUnreadCount,
    clearError,
    getNotificationsByType,
    getUrgentNotifications,
  };
};
